import type { ColDef, ICellRendererParams } from 'ag-grid-community';

import {
  DEVICE_TYPE_LIMA,
  DEVICE_TYPES_CONFIG
} from './DevicesTable.deviceTypes';
import {
  DeviceNameInnerCellRenderer,
  renderActionsCell,
  renderDeviceTypeCell,
  renderIpAddressCell,
  renderLastSeenCell,
  renderOrganizationCell,
  SourcesCountCellRenderer,
  StatusCellRenderer
} from './DevicesTable.renderers';
import type { DeviceTreeNode } from './DevicesTable.types';

const compareNullableNumbers = (
  valueA: number | null,
  valueB: number | null
) => {
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return -1;
  if (valueB == null) return 1;
  return valueA - valueB;
};

const parseIpv4ToNumber = (ipAddress?: string | null): number | null => {
  if (!ipAddress) return null;

  const parts = ipAddress.trim().split('.');

  if (parts.length !== 4) {
    return null;
  }

  const octets = parts.map((part) => Number(part));
  if (octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
    return null;
  }

  return (
    octets[0] * 256 ** 3 + octets[1] * 256 ** 2 + octets[2] * 256 + octets[3]
  );
};

type DeviceComparator = NonNullable<ColDef<DeviceTreeNode>['comparator']>;

// AG Grid default string sorting is lexicographic, which misorders IPv4 values
// (e.g., "10.0.0.2" before "2.0.0.1"). Normalize to numeric form first.
const ipAddressComparator: DeviceComparator = (
  valueA,
  valueB,
  nodeA,
  nodeB
) => {
  const ipA = nodeA.data?.ipAddress ?? valueA;
  const ipB = nodeB.data?.ipAddress ?? valueB;
  const parsedA = parseIpv4ToNumber(ipA);
  const parsedB = parseIpv4ToNumber(ipB);

  if (parsedA != null || parsedB != null) {
    return compareNullableNumbers(parsedA, parsedB);
  }

  return (ipA || '').localeCompare(ipB || '', undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

const toTimestamp = (value?: string | null): number | null => {
  if (!value) return null;

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const resolveLastSeenValue = (
  row: DeviceTreeNode | undefined,
  fallback: string | undefined
): number | null => {
  const rawLastSeen =
    row?.deviceData?.last_seen ??
    row?.sourceData?.last_seen ??
    row?.providerData?.last_seen;

  return toTimestamp(rawLastSeen ?? row?.lastSeen ?? fallback);
};

// Rows may carry last-seen values from different nested objects and formats.
// Parse to epoch milliseconds so AG Grid sorts by actual chronology.
const lastSeenComparator: DeviceComparator = (valueA, valueB, nodeA, nodeB) => {
  const lastSeenA = resolveLastSeenValue(nodeA.data, valueA);
  const lastSeenB = resolveLastSeenValue(nodeB.data, valueB);

  return compareNullableNumbers(lastSeenA, lastSeenB);
};

/**
 * Auto-group column definition for device/source names
 */
export const autoGroupColumnDef: ColDef<DeviceTreeNode> = {
  headerName: 'Device Name',
  field: 'name',
  minWidth: 250,
  maxWidth: 350,
  width: 350,
  cellClass: 'ellipsis-cell',
  cellRenderer: 'agGroupCellRenderer',
  suppressHeaderMenuButton: true,
  cellRendererParams: {
    suppressCount: true,
    suppressDoubleClickExpand: false,
    suppressEnterExpand: false,
    innerRenderer: DeviceNameInnerCellRenderer,
    suppressExpandIcon: (params: ICellRendererParams<DeviceTreeNode>) => {
      const node = params.data;
      return !(
        (node?.type === 'device' &&
          node?.deviceData?.device_type_id === DEVICE_TYPE_LIMA &&
          node?.hasChildren) ||
        (node?.type === 'source' && node?.level === 1 && node?.hasChildren)
      );
    }
  }
};

/**
 * Column definitions for Lima Devices Table
 */
export const createDeviceColumnDefs = (
  selectedOrganizationId: number,
  isMainDevicePage?: boolean
): ColDef<DeviceTreeNode>[] => {
  const cols: ColDef<DeviceTreeNode>[] = [
    {
      field: 'deviceType',
      headerName: 'Device Type',
      hide: !isMainDevicePage,
      sortable: true,
      filter: false,
      resizable: true,
      flex: 2,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      cellRenderer: renderDeviceTypeCell
    },
    {
      field: 'ipAddress',
      headerName: 'IP Address',
      sortable: true,
      filter: false,
      resizable: true,
      flex: 2,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      comparator: ipAddressComparator,
      cellRenderer: renderIpAddressCell
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      filter: false,
      resizable: true,
      minWidth: 120,
      flex: 1,
      suppressHeaderMenuButton: true,
      cellRenderer: StatusCellRenderer
    },
    {
      field: 'lastSeen',
      headerName: 'Last Connection',
      sortable: true,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      comparator: lastSeenComparator,
      cellRenderer: renderLastSeenCell
    }
  ];

  // Add organization column if viewing all organizations
  if (selectedOrganizationId === 0) {
    cols.push({
      field: 'organization',
      headerName: 'Organization',
      sortable: true,
      filter: false,
      resizable: true,
      flex: 2,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      cellRenderer: renderOrganizationCell
    });
  }

  // Add sources count and actions columns
  cols.push(
    {
      field: 'sourceCount',
      headerName: 'Sources',
      sortable: true,
      filter: false,
      resizable: true,
      width: 150,
      flex: 1,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      cellRenderer: SourcesCountCellRenderer,
      valueGetter: (params: { data?: DeviceTreeNode }) => {
        return params.data?.sourceCount;
      }
    },
    {
      colId: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filter: false,
      resizable: false,
      suppressNavigable: true,
      editable: false,
      suppressHeaderMenuButton: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default'
      },
      suppressKeyboardEvent: () => true,
      cellRenderer: renderActionsCell
    }
  );

  return cols;
};

export { DEVICE_TYPE_LIMA };
export default DEVICE_TYPES_CONFIG;
